import React from 'react';

import styled from 'styled-components';
import { COLORS } from '@joinforma/ui/constants/legacy/theme';
import Title1 from '@joinforma/ui/components/Title1';
import Body1 from '@joinforma/ui/components/Body1';
import Caption from '@joinforma/ui/components/Caption';

import { ReactComponent as LockIcon } from '@joinforma/ui/assets/icons/legacy/lock.svg';

import DEBIT_CARD from 'assets/images/enabled-debitcard.svg';
import VIRTUAL_CARD from 'assets/images/enabled-virtualcard.svg';

import { CARD_TYPES } from 'constants/stripe';
import { PRIVACY } from 'constants/smartlook';

interface ContainerProps {
  card_type: CARD_TYPES;
}

const Container = styled.div<ContainerProps>`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background-image: url(${(props) =>
    props.card_type === CARD_TYPES.VIRTUAL ? VIRTUAL_CARD : DEBIT_CARD});
  background-position: center;
  background-size: contain;
  width: 314px;
  height: 198px;
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  box-shadow: 0 24px 60px -20px rgba(34, 34, 45, 0.5);
`;

const LockedCardOverlay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${`${COLORS.BLACK_100}75`};
`;

const BigLockIcon = styled(LockIcon)`
  width: 48px;
  height: 48px;
  path {
    stroke: ${COLORS.WHITE};
  }
`;

const CardNumberContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  margin-bottom: 31px;
  width: 100%;
`;

const FourDigits = styled(Title1)`
  color: ${COLORS.WHITE};
  display: inline-block;
  margin: 0 6px;
  font-weight: normal;
`;

const FullName = styled(Body1).attrs({ bold: true })`
  color: ${COLORS.WHITE};
  margin-bottom: 3px;
`;

const CardInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const MetaInfoContainer = styled.div`
  display: flex;
`;

const MetaAttributeContainer = styled.div`
  margin-right: 16px;
`;

const MetaAttributeName = styled(Caption).attrs({ bold: true })`
  color: ${COLORS.WHITE};
  margin-right: 8px;
  text-transform: uppercase;
`;

const MetaAttributeValue = styled(Caption)`
  color: ${COLORS.WHITE};
`;

const renderOverlay = () => (
  <LockedCardOverlay>
    <BigLockIcon />
  </LockedCardOverlay>
);

const DebitCard = ({
  is_locked,
  card_type,
  full_name,
  exp_month,
  exp_year,
  cvc,
  number,
}: $TSFixMe) =>
  card_type === CARD_TYPES.VIRTUAL ? (
    <Container card_type={CARD_TYPES.VIRTUAL}>
      <CardNumberContainer data-sl={PRIVACY.MASK}>
        <FourDigits>{number.slice(0, 4)}</FourDigits>
        <FourDigits>{number.slice(4, 8)}</FourDigits>
        <FourDigits>{number.slice(8, 12)}</FourDigits>
        <FourDigits>{number.slice(12, 16)}</FourDigits>
      </CardNumberContainer>
      <CardInfoContainer>
        <FullName>{full_name}</FullName>
        <MetaInfoContainer>
          <MetaAttributeContainer>
            <MetaAttributeName>Exp</MetaAttributeName>
            <MetaAttributeValue>
              {exp_month}/{exp_year}
            </MetaAttributeValue>
          </MetaAttributeContainer>
          <MetaAttributeContainer>
            <MetaAttributeName>CVV</MetaAttributeName>
            <MetaAttributeValue>{cvc}</MetaAttributeValue>
          </MetaAttributeContainer>
        </MetaInfoContainer>
      </CardInfoContainer>
      {is_locked && renderOverlay()}
    </Container>
  ) : (
    <Container card_type={CARD_TYPES.PHYSICAL}>
      {is_locked && renderOverlay()}
    </Container>
  );

export default DebitCard;
